import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";
import { config } from "../../utils/apiUrl";


/* =========================================================
   API
========================================================= */

const api = new API();


/* =========================================================
   EMPTY SUMMARY
========================================================= */

export const EMPTY_ORDER_SUMMARY = {
  totalOrders: 0,

  totalAmount: 0,

  statuses: {
    pending: 0,
    confirmed: 0,
    processing: 0,
    packed: 0,
    shipped: 0,
    out_for_delivery: 0,
    delivered: 0,
    cancelled: 0,
    returned: 0,
  },

  payments: {
    pending: {
      count: 0,
      amount: 0,
    },

    paid: {
      count: 0,
      amount: 0,
    },

    failed: {
      count: 0,
      amount: 0,
    },

    refunded: {
      count: 0,
      amount: 0,
    },

    partially_refunded: {
      count: 0,
      amount: 0,
    },
  },
};


/* =========================================================
   DEFAULT FILTERS
========================================================= */

export const EMPTY_ORDER_FILTERS = {
  search: "",

  status: "",

  paymentStatus: "",

  paymentMethod: "",

  datePreset: "this_month",

  dateFrom: "",

  dateTo: "",
};


/* =========================================================
   DEFAULT PAGINATION
========================================================= */

export const EMPTY_ORDER_PAGINATION = {
  page: 1,

  limit: 10,

  total: 0,

  totalPages: 1,

  hasNextPage: false,

  hasPrevPage: false,
};


/* =========================================================
   ERROR MESSAGE
========================================================= */

const getErrorMessage = (
  error,
  fallback
) => {
  return (
    error?.response
      ?.data
      ?.message ||
    error?.message ||
    fallback
  );
};


/* =========================================================
   NORMALIZE SUMMARY
========================================================= */

const normalizeSummary = (
  summary
) => {
  return {
    ...EMPTY_ORDER_SUMMARY,

    ...(summary || {}),

    statuses: {
      ...EMPTY_ORDER_SUMMARY.statuses,

      ...(summary?.statuses ||
        {}),
    },

    payments: {
      ...EMPTY_ORDER_SUMMARY.payments,

      ...(summary?.payments ||
        {}),

      pending: {
        ...EMPTY_ORDER_SUMMARY
          .payments
          .pending,

        ...(summary
          ?.payments
          ?.pending ||
          {}),
      },

      paid: {
        ...EMPTY_ORDER_SUMMARY
          .payments
          .paid,

        ...(summary
          ?.payments
          ?.paid ||
          {}),
      },

      failed: {
        ...EMPTY_ORDER_SUMMARY
          .payments
          .failed,

        ...(summary
          ?.payments
          ?.failed ||
          {}),
      },

      refunded: {
        ...EMPTY_ORDER_SUMMARY
          .payments
          .refunded,

        ...(summary
          ?.payments
          ?.refunded ||
          {}),
      },

      partially_refunded: {
        ...EMPTY_ORDER_SUMMARY
          .payments
          .partially_refunded,

        ...(summary
          ?.payments
          ?.partially_refunded ||
          {}),
      },
    },
  };
};


/* =========================================================
   NORMALIZE PAGINATION
========================================================= */

const normalizePagination = (
  pagination
) => {
  return {
    ...EMPTY_ORDER_PAGINATION,

    ...(pagination || {}),

    page:
      Number(
        pagination?.page ||
        1
      ),

    limit:
      Number(
        pagination?.limit ||
        10
      ),

    total:
      Number(
        pagination?.total ||
        0
      ),

    totalPages:
      Math.max(
        Number(
          pagination
            ?.totalPages ||
          1
        ),
        1
      ),

    hasNextPage:
      Boolean(
        pagination
          ?.hasNextPage
      ),

    hasPrevPage:
      Boolean(
        pagination
          ?.hasPrevPage
      ),
  };
};


/* =========================================================
   UPDATE ORDER IN LIST
========================================================= */

const replaceOrderInList = (
  orders,
  updatedOrder
) => {
  if (
    !updatedOrder?._id
  ) {
    return orders;
  }


  return orders.map(
    (order) =>
      String(
        order?._id
      ) ===
      String(
        updatedOrder._id
      )
        ? {
            ...order,
            ...updatedOrder,
          }
        : order
  );
};


/* =========================================================
   EXTRACT ORDER FROM RESPONSE

   Some endpoints return:

   data: order

   Cancel / Return return:

   data: {
     order,
     refundRequired
   }
========================================================= */

const extractOrder = (
  response
) => {
  return (
    response?.data?.order ||
    response?.data ||
    null
  );
};


/* =========================================================
   FETCH ORDERS
========================================================= */

export const fetchOrders =
  createAsyncThunk(
    "orderManagement/fetchOrders",

    async (
      params = {},
      {
        rejectWithValue,
      }
    ) => {
      try {
        const query =
          new URLSearchParams();


        Object.entries(
          params
        ).forEach(
          ([
            key,
            value,
          ]) => {
            if (
              value !==
                undefined &&
              value !==
                null &&
              value !==
                ""
            ) {
              query.set(
                key,
                value
              );
            }
          }
        );


        const queryString =
          query.toString();


        const url =
          queryString
            ? `${config.getOrders}?${queryString}`
            : config.getOrders;


        const response =
          await api.get(
            url
          );


        if (
          response?.status !==
            "success" &&
          response?.success !==
            true
        ) {
          throw new Error(
            response?.message ||
              "Unable to fetch orders."
          );
        }


        return response;

      } catch (
        error
      ) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Unable to fetch orders."
          )
        );
      }
    }
  );


/* =========================================================
   FETCH ORDER DETAILS
========================================================= */

export const fetchOrderById =
  createAsyncThunk(
    "orderManagement/fetchOrderById",

    async (
      orderId,
      {
        rejectWithValue,
      }
    ) => {
      try {
        if (!orderId) {
          throw new Error(
            "Order ID is required."
          );
        }


        const response =
          await api.get(
            config.getOrderById(
              orderId
            )
          );


        if (
          response?.status !==
            "success" &&
          response?.success !==
            true
        ) {
          throw new Error(
            response?.message ||
              "Unable to fetch order details."
          );
        }


        return response?.data;

      } catch (
        error
      ) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Unable to fetch order details."
          )
        );
      }
    }
  );


/* =========================================================
   ACCEPT ORDER
========================================================= */

export const acceptOrder =
  createAsyncThunk(
    "orderManagement/acceptOrder",

    async (
      {
        orderId,
        note = "",
      },
      {
        rejectWithValue,
      }
    ) => {
      try {
        const response =
          await api.patch(
            config.acceptOrder(
              orderId
            ),
            {
              note,
            }
          );


        if (
          response?.status !==
            "success" &&
          response?.success !==
            true
        ) {
          throw new Error(
            response?.message ||
              "Unable to accept order."
          );
        }


        return {
          order:
            extractOrder(
              response
            ),

          message:
            response?.message ||
            "Order accepted successfully.",
        };

      } catch (
        error
      ) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Unable to accept order."
          )
        );
      }
    }
  );


/* =========================================================
   UPDATE ORDER STATUS
========================================================= */

export const updateOrderStatus =
  createAsyncThunk(
    "orderManagement/updateOrderStatus",

    async (
      {
        orderId,
        status,
        note = "",
      },
      {
        rejectWithValue,
      }
    ) => {
      try {
        const response =
          await api.patch(
            config.updateOrderStatus(
              orderId
            ),
            {
              status,
              note,
            }
          );


        if (
          response?.status !==
            "success" &&
          response?.success !==
            true
        ) {
          throw new Error(
            response?.message ||
              "Unable to update order status."
          );
        }


        return {
          order:
            extractOrder(
              response
            ),

          message:
            response?.message ||
            "Order status updated successfully.",
        };

      } catch (
        error
      ) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Unable to update order status."
          )
        );
      }
    }
  );


/* =========================================================
   UPDATE TRACKING
========================================================= */

export const updateOrderTracking =
  createAsyncThunk(
    "orderManagement/updateOrderTracking",

    async (
      {
        orderId,

        courierName = "",

        trackingNumber = "",

        trackingUrl = "",

        estimatedDeliveryDate =
          "",

        shippingNote = "",
      },
      {
        rejectWithValue,
      }
    ) => {
      try {
        const response =
          await api.patch(
            config.updateOrderTracking(
              orderId
            ),
            {
              courierName,

              trackingNumber,

              trackingUrl,

              estimatedDeliveryDate,

              shippingNote,
            }
          );


        if (
          response?.status !==
            "success" &&
          response?.success !==
            true
        ) {
          throw new Error(
            response?.message ||
              "Unable to update tracking details."
          );
        }


        return {
          order:
            extractOrder(
              response
            ),

          message:
            response?.message ||
            "Tracking details updated successfully.",
        };

      } catch (
        error
      ) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Unable to update tracking details."
          )
        );
      }
    }
  );


/* =========================================================
   SHIP ORDER
========================================================= */

export const shipOrder =
  createAsyncThunk(
    "orderManagement/shipOrder",

    async (
      {
        orderId,

        courierName,

        trackingNumber,

        trackingUrl = "",

        estimatedDeliveryDate =
          "",

        shippingNote = "",

        note = "",
      },
      {
        rejectWithValue,
      }
    ) => {
      try {
        const response =
          await api.patch(
            config.shipOrder(
              orderId
            ),
            {
              courierName,

              trackingNumber,

              trackingUrl,

              estimatedDeliveryDate,

              shippingNote,

              note,
            }
          );


        if (
          response?.status !==
            "success" &&
          response?.success !==
            true
        ) {
          throw new Error(
            response?.message ||
              "Unable to ship order."
          );
        }


        return {
          order:
            extractOrder(
              response
            ),

          message:
            response?.message ||
            "Order shipped successfully.",
        };

      } catch (
        error
      ) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Unable to ship order."
          )
        );
      }
    }
  );


/* =========================================================
   CANCEL ORDER
========================================================= */

export const cancelOrder =
  createAsyncThunk(
    "orderManagement/cancelOrder",

    async (
      {
        orderId,
        reason,
        note = "",
      },
      {
        rejectWithValue,
      }
    ) => {
      try {
        const response =
          await api.patch(
            config.cancelOrder(
              orderId
            ),
            {
              reason,
              note,
            }
          );


        if (
          response?.status !==
            "success" &&
          response?.success !==
            true
        ) {
          throw new Error(
            response?.message ||
              "Unable to cancel order."
          );
        }


        return {
          order:
            extractOrder(
              response
            ),

          refundRequired:
            Boolean(
              response
                ?.data
                ?.refundRequired
            ),

          inventoryStatus:
            response
              ?.data
              ?.inventoryStatus ||
            response
              ?.data
              ?.order
              ?.inventoryStatus ||
            "",

          message:
            response?.message ||
            "Order cancelled successfully.",
        };

      } catch (
        error
      ) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Unable to cancel order."
          )
        );
      }
    }
  );


/* =========================================================
   RETURN ORDER
========================================================= */

export const returnOrder =
  createAsyncThunk(
    "orderManagement/returnOrder",

    async (
      {
        orderId,
        reason,
        note = "",
      },
      {
        rejectWithValue,
      }
    ) => {
      try {
        const response =
          await api.patch(
            config.returnOrder(
              orderId
            ),
            {
              reason,
              note,
            }
          );


        if (
          response?.status !==
            "success" &&
          response?.success !==
            true
        ) {
          throw new Error(
            response?.message ||
              "Unable to return order."
          );
        }


        return {
          order:
            extractOrder(
              response
            ),

          refundRequired:
            Boolean(
              response
                ?.data
                ?.refundRequired
            ),

          message:
            response?.message ||
            "Order returned successfully.",
        };

      } catch (
        error
      ) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Unable to return order."
          )
        );
      }
    }
  );


/* =========================================================
   UPDATE ORDER NOTES
========================================================= */

export const updateOrderNotes =
  createAsyncThunk(
    "orderManagement/updateOrderNotes",

    async (
      {
        orderId,
        notes,
      },
      {
        rejectWithValue,
      }
    ) => {
      try {
        const response =
          await api.patch(
            config.updateOrderNotes(
              orderId
            ),
            {
              notes,
            }
          );


        if (
          response?.status !==
            "success" &&
          response?.success !==
            true
        ) {
          throw new Error(
            response?.message ||
              "Unable to update order notes."
          );
        }


        return {
          order:
            extractOrder(
              response
            ),

          message:
            response?.message ||
            "Order notes updated successfully.",
        };

      } catch (
        error
      ) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Unable to update order notes."
          )
        );
      }
    }
  );


/* =========================================================
   INITIAL STATE
========================================================= */

const initialState = {
  /* =======================================================
     LIST
  ======================================================= */

  orders: [],


  /* =======================================================
     SUMMARY / DASHBOARD
  ======================================================= */

  summary: {
    ...EMPTY_ORDER_SUMMARY,
  },


  /* =======================================================
     FILTERS

     filters:
       current form values

     appliedFilters:
       values currently sent to backend
  ======================================================= */

  filters: {
    ...EMPTY_ORDER_FILTERS,
  },

  appliedFilters: {
    ...EMPTY_ORDER_FILTERS,
  },


  /* =======================================================
     PAGINATION
  ======================================================= */

  pagination: {
    ...EMPTY_ORDER_PAGINATION,
  },


  /* =======================================================
     SELECTED ORDER
  ======================================================= */

  selectedOrder: null,


  /* =======================================================
     LIST LOADING
  ======================================================= */

  loading: false,

  error: "",


  /* =======================================================
     DETAILS
  ======================================================= */

  detailsLoading: false,

  detailsError: "",


  /* =======================================================
     ACTION STATE
  ======================================================= */

  actionLoading: false,

  actionType: "",

  actionOrderId: null,

  actionError: "",

  actionMessage: "",


  /* =======================================================
     REFUND INFORMATION

     Used after cancellation / return.
  ======================================================= */

  refundRequired: false,


  /* =======================================================
     LAST REFRESH
  ======================================================= */

  lastUpdated: null,
};


/* =========================================================
   SLICE
========================================================= */

const orderManagementSlice =
  createSlice({
    name:
      "orderManagement",

    initialState,

    reducers: {
      /* ===================================================
         FILTER FIELD
      =================================================== */

      setOrderFilter: (
        state,
        action
      ) => {
        const {
          key,
          value,
        } =
          action.payload ||
          {};


        if (!key) {
          return;
        }


        state.filters[
          key
        ] = value;
      },


      /* ===================================================
         SET ALL FILTERS
      =================================================== */

      setOrderFilters: (
        state,
        action
      ) => {
        state.filters = {
          ...state.filters,

          ...(action.payload ||
            {}),
        };
      },


      /* ===================================================
         APPLY FILTERS
      =================================================== */

      applyOrderFilters: (
        state
      ) => {
        state.appliedFilters = {
          ...state.filters,
        };


        state.pagination.page =
          1;
      },


      /* ===================================================
         RESET FILTERS
      =================================================== */

      resetOrderFilters: (
        state
      ) => {
        state.filters = {
          ...EMPTY_ORDER_FILTERS,
        };


        state.appliedFilters = {
          ...EMPTY_ORDER_FILTERS,
        };


        state.pagination.page =
          1;
      },


      /* ===================================================
         STATUS TAB

         Allows dashboard/status cards to change only
         order status while preserving date/search filters.
      =================================================== */

      setOrderStatusFilter: (
        state,
        action
      ) => {
        const status =
          action.payload ||
          "";


        state.filters.status =
          status;


        state.appliedFilters.status =
          status;


        state.pagination.page =
          1;
      },


      /* ===================================================
         DATE RANGE
      =================================================== */

      setOrderDateRange: (
        state,
        action
      ) => {
        const {
          preset = "",
          dateFrom = "",
          dateTo = "",
        } =
          action.payload ||
          {};


        state.filters.datePreset =
          preset;


        state.filters.dateFrom =
          dateFrom;


        state.filters.dateTo =
          dateTo;


        state.appliedFilters.datePreset =
          preset;


        state.appliedFilters.dateFrom =
          dateFrom;


        state.appliedFilters.dateTo =
          dateTo;


        state.pagination.page =
          1;
      },


      /* ===================================================
         PAGE
      =================================================== */

      setOrderPage: (
        state,
        action
      ) => {
        const page =
          Number(
            action.payload
          );


        if (
          Number.isInteger(
            page
          ) &&
          page > 0
        ) {
          state.pagination.page =
            page;
        }
      },


      /* ===================================================
         LIMIT
      =================================================== */

      setOrderLimit: (
        state,
        action
      ) => {
        const limit =
          Number(
            action.payload
          );


        if (
          [
            10,
            20,
            50,
            100,
          ].includes(
            limit
          )
        ) {
          state.pagination.limit =
            limit;


          state.pagination.page =
            1;
        }
      },


      /* ===================================================
         SET SELECTED ORDER
      =================================================== */

      setSelectedOrder: (
        state,
        action
      ) => {
        state.selectedOrder =
          action.payload ||
          null;


        state.detailsError =
          "";
      },


      /* ===================================================
         CLEAR SELECTED ORDER
      =================================================== */

      clearSelectedOrder: (
        state
      ) => {
        state.selectedOrder =
          null;


        state.detailsError =
          "";
      },


      /* ===================================================
         CLEAR ACTION MESSAGE
      =================================================== */

      clearOrderActionMessage: (
        state
      ) => {
        state.actionMessage =
          "";


        state.actionError =
          "";


        state.refundRequired =
          false;
      },


      /* ===================================================
         CLEAR ALL
      =================================================== */

      resetOrderManagement: (
        state
      ) => {
        Object.assign(
          state,
          initialState
        );
      },
    },


    /* =====================================================
       EXTRA REDUCERS
    ===================================================== */

    extraReducers: (
      builder
    ) => {
      /* ===================================================
         FETCH ORDERS
      =================================================== */

      builder
        .addCase(
          fetchOrders.pending,
          (
            state
          ) => {
            state.loading =
              true;


            state.error =
              "";
          }
        )

        .addCase(
          fetchOrders.fulfilled,
          (
            state,
            action
          ) => {
            state.loading =
              false;


            state.orders =
              Array.isArray(
                action.payload
                  ?.data
              )
                ? action.payload
                    .data
                : [];


            state.pagination =
              normalizePagination(
                action.payload
                  ?.pagination
              );


            state.summary =
              normalizeSummary(
                action.payload
                  ?.summary
              );


            state.lastUpdated =
              new Date()
                .toISOString();
          }
        )

        .addCase(
          fetchOrders.rejected,
          (
            state,
            action
          ) => {
            state.loading =
              false;


            state.error =
              action.payload ||
              "Unable to fetch orders.";
          }
        );


      /* ===================================================
         FETCH ORDER DETAILS
      =================================================== */

      builder
        .addCase(
          fetchOrderById.pending,
          (
            state
          ) => {
            state.detailsLoading =
              true;


            state.detailsError =
              "";
          }
        )

        .addCase(
          fetchOrderById.fulfilled,
          (
            state,
            action
          ) => {
            state.detailsLoading =
              false;


            state.selectedOrder =
              action.payload ||
              null;
          }
        )

        .addCase(
          fetchOrderById.rejected,
          (
            state,
            action
          ) => {
            state.detailsLoading =
              false;


            state.detailsError =
              action.payload ||
              "Unable to fetch order details.";
          }
        );


      /* ===================================================
         ACTION PENDING HELPER
      =================================================== */

      const actionPending = (
        state,
        actionType,
        orderId
      ) => {
        state.actionLoading =
          true;


        state.actionType =
          actionType;


        state.actionOrderId =
          orderId ||
          null;


        state.actionError =
          "";


        state.actionMessage =
          "";


        state.refundRequired =
          false;
      };


      /* ===================================================
         ACTION SUCCESS HELPER
      =================================================== */

      const actionSuccess = (
        state,
        payload
      ) => {
        state.actionLoading =
          false;


        state.actionError =
          "";


        state.actionMessage =
          payload?.message ||
          "Order updated successfully.";


        state.actionType =
          "";


        state.actionOrderId =
          null;


        const updatedOrder =
          payload?.order;


        if (
          updatedOrder?._id
        ) {
          state.orders =
            replaceOrderInList(
              state.orders,
              updatedOrder
            );


          if (
            String(
              state
                .selectedOrder
                ?._id ||
              ""
            ) ===
            String(
              updatedOrder._id
            )
          ) {
            state.selectedOrder = {
              ...state.selectedOrder,

              ...updatedOrder,
            };
          }
        }


        state.lastUpdated =
          new Date()
            .toISOString();
      };


      /* ===================================================
         ACTION ERROR HELPER
      =================================================== */

      const actionRejected = (
        state,
        action
      ) => {
        state.actionLoading =
          false;


        state.actionType =
          "";


        state.actionOrderId =
          null;


        state.actionError =
          action.payload ||
          "Unable to update order.";
      };


      /* ===================================================
         ACCEPT
      =================================================== */

      builder
        .addCase(
          acceptOrder.pending,
          (
            state,
            action
          ) => {
            actionPending(
              state,
              "accept",
              action.meta
                ?.arg
                ?.orderId
            );
          }
        )

        .addCase(
          acceptOrder.fulfilled,
          (
            state,
            action
          ) => {
            actionSuccess(
              state,
              action.payload
            );
          }
        )

        .addCase(
          acceptOrder.rejected,
          actionRejected
        );


      /* ===================================================
         STATUS
      =================================================== */

      builder
        .addCase(
          updateOrderStatus.pending,
          (
            state,
            action
          ) => {
            actionPending(
              state,
              "status",
              action.meta
                ?.arg
                ?.orderId
            );
          }
        )

        .addCase(
          updateOrderStatus.fulfilled,
          (
            state,
            action
          ) => {
            actionSuccess(
              state,
              action.payload
            );
          }
        )

        .addCase(
          updateOrderStatus.rejected,
          actionRejected
        );


      /* ===================================================
         TRACKING
      =================================================== */

      builder
        .addCase(
          updateOrderTracking.pending,
          (
            state,
            action
          ) => {
            actionPending(
              state,
              "tracking",
              action.meta
                ?.arg
                ?.orderId
            );
          }
        )

        .addCase(
          updateOrderTracking.fulfilled,
          (
            state,
            action
          ) => {
            actionSuccess(
              state,
              action.payload
            );
          }
        )

        .addCase(
          updateOrderTracking.rejected,
          actionRejected
        );


      /* ===================================================
         SHIP
      =================================================== */

      builder
        .addCase(
          shipOrder.pending,
          (
            state,
            action
          ) => {
            actionPending(
              state,
              "ship",
              action.meta
                ?.arg
                ?.orderId
            );
          }
        )

        .addCase(
          shipOrder.fulfilled,
          (
            state,
            action
          ) => {
            actionSuccess(
              state,
              action.payload
            );
          }
        )

        .addCase(
          shipOrder.rejected,
          actionRejected
        );


      /* ===================================================
         CANCEL
      =================================================== */

      builder
        .addCase(
          cancelOrder.pending,
          (
            state,
            action
          ) => {
            actionPending(
              state,
              "cancel",
              action.meta
                ?.arg
                ?.orderId
            );
          }
        )

        .addCase(
          cancelOrder.fulfilled,
          (
            state,
            action
          ) => {
            actionSuccess(
              state,
              action.payload
            );


            state.refundRequired =
              Boolean(
                action
                  .payload
                  ?.refundRequired
              );
          }
        )

        .addCase(
          cancelOrder.rejected,
          actionRejected
        );


      /* ===================================================
         RETURN
      =================================================== */

      builder
        .addCase(
          returnOrder.pending,
          (
            state,
            action
          ) => {
            actionPending(
              state,
              "return",
              action.meta
                ?.arg
                ?.orderId
            );
          }
        )

        .addCase(
          returnOrder.fulfilled,
          (
            state,
            action
          ) => {
            actionSuccess(
              state,
              action.payload
            );


            state.refundRequired =
              Boolean(
                action
                  .payload
                  ?.refundRequired
              );
          }
        )

        .addCase(
          returnOrder.rejected,
          actionRejected
        );


      /* ===================================================
         NOTES
      =================================================== */

      builder
        .addCase(
          updateOrderNotes.pending,
          (
            state,
            action
          ) => {
            actionPending(
              state,
              "notes",
              action.meta
                ?.arg
                ?.orderId
            );
          }
        )

        .addCase(
          updateOrderNotes.fulfilled,
          (
            state,
            action
          ) => {
            actionSuccess(
              state,
              action.payload
            );
          }
        )

        .addCase(
          updateOrderNotes.rejected,
          actionRejected
        );
    },
  });


/* =========================================================
   ACTIONS
========================================================= */

export const {
  setOrderFilter,

  setOrderFilters,

  applyOrderFilters,

  resetOrderFilters,

  setOrderStatusFilter,

  setOrderDateRange,

  setOrderPage,

  setOrderLimit,

  setSelectedOrder,

  clearSelectedOrder,

  clearOrderActionMessage,

  resetOrderManagement,
} =
  orderManagementSlice.actions;


/* =========================================================
   SELECTORS
========================================================= */

export const selectOrderManagement =
  (state) =>
    state.orderManagement;


export const selectOrders =
  (state) =>
    state.orderManagement
      ?.orders ||
    [];


export const selectOrderSummary =
  (state) =>
    state.orderManagement
      ?.summary ||
    EMPTY_ORDER_SUMMARY;


export const selectOrderFilters =
  (state) =>
    state.orderManagement
      ?.filters ||
    EMPTY_ORDER_FILTERS;


export const selectAppliedOrderFilters =
  (state) =>
    state.orderManagement
      ?.appliedFilters ||
    EMPTY_ORDER_FILTERS;


export const selectOrderPagination =
  (state) =>
    state.orderManagement
      ?.pagination ||
    EMPTY_ORDER_PAGINATION;


export const selectSelectedOrder =
  (state) =>
    state.orderManagement
      ?.selectedOrder ||
    null;


export const selectOrderLoading =
  (state) =>
    Boolean(
      state.orderManagement
        ?.loading
    );


export const selectOrderDetailsLoading =
  (state) =>
    Boolean(
      state.orderManagement
        ?.detailsLoading
    );


export const selectOrderActionState =
  (state) => ({
    loading:
      Boolean(
        state.orderManagement
          ?.actionLoading
      ),

    type:
      state.orderManagement
        ?.actionType ||
      "",

    orderId:
      state.orderManagement
        ?.actionOrderId ||
      null,

    error:
      state.orderManagement
        ?.actionError ||
      "",

    message:
      state.orderManagement
        ?.actionMessage ||
      "",

    refundRequired:
      Boolean(
        state.orderManagement
          ?.refundRequired
      ),
  });


/* =========================================================
   REDUCER
========================================================= */

export default
  orderManagementSlice.reducer;